import { Action, Dispatch, Reducer } from 'redux';
import { EmptyRequestState, RequestState } from '../models/RequestState';
import { CoreGoogleUserTrackingUpdate, CoreGoogleUserTrackingConflict, CoreGoogleUserTrackingMapping, CoreEntraIdUserTrackingConflict, CoreEntraIdUserTrackingUpdate, CoreEntraIdUserTrackingMapping, CoreAsmUserTrackingConflict, CoreAsmUserTrackingMapping } from '../models/CoreTrackingOperation';
import StatusCode from '../util/StatusCode';
import InstanceClient from '../services/InstanceClient';

enum TrackingOperationType {
    UPDATE_GOOGLE_USER_TRACKING = 'UPDATE_GOOGLE_USER_TRACKING',
    INACTIVATE_GOOGLE_USER_TRACKING = 'INACTIVATE_GOOGLE_USER_TRACKING',
    RESTORE_GOOGLE_USER_TRACKING = 'RESTORE_GOOGLE_USER_TRACKING',
    MAP_GOOGLE_USER_TRACKING = 'MAP_GOOGLE_USER_TRACKING',
    UPGRADE_GOOGLE_USER_TRACKING = 'UPGRADE_GOOGLE_USER_TRACKING',    
    UPDATE_ENTRAID_USER_TRACKING = 'UPDATE_ENTRAID_USER_TRACKING',
    INACTIVATE_ENTRAID_USER_TRACKING = 'INACTIVATE_ENTRAID_USER_TRACKING',
    RESTORE_ENTRAID_USER_TRACKING = 'RESTORE_ENTRAID_USER_TRACKING',
    MAP_ENTRAID_USER_TRACKING = 'MAP_ENTRAID_USER_TRACKING',
    INACTIVATE_ASM_USER_TRACKING = 'INACTIVATE_ASM_USER_TRACKING',
    RESTORE_ASM_USER_TRACKING = 'RESTORE_ASM_USER_TRACKING',
    MAP_ASM_USER_TRACKING = 'MAP_ASM_USER_TRACKING',
}

export interface TrackingOperationState {
    updateGoogleUserTracking: RequestState<CoreGoogleUserTrackingConflict[]>
    inactivateGoogleUserTracking: EmptyRequestState
    restoreGoogleUserTracking: RequestState<CoreGoogleUserTrackingConflict[]>
    mapGoogleUserTracking: RequestState<CoreGoogleUserTrackingConflict[]>
    upgradeGoogleUserTracking: RequestState<CoreGoogleUserTrackingConflict[]>
    updateEntraIdUserTracking: RequestState<CoreEntraIdUserTrackingConflict[]>
    inactivateEntraIdUserTracking: EmptyRequestState
    restoreEntraIdUserTracking: RequestState<CoreEntraIdUserTrackingConflict[]>
    mapEntraIdUserTracking: RequestState<CoreEntraIdUserTrackingConflict[]>
    inactivateAsmUserTracking: EmptyRequestState
    restoreAsmUserTracking: RequestState<CoreAsmUserTrackingConflict[]>
    mapAsmUserTracking: RequestState<CoreAsmUserTrackingConflict[]>
}

interface Tracking_UpdateGoogleUserTracking extends Action<TrackingOperationType> {
    type: TrackingOperationType.UPDATE_GOOGLE_USER_TRACKING
    updateGoogleUserTracking: RequestState<CoreGoogleUserTrackingConflict[]>
}

interface Tracking_InactivateGoogleUserTracking extends Action<TrackingOperationType> {
    type: TrackingOperationType.INACTIVATE_GOOGLE_USER_TRACKING
    inactivateGoogleUserTracking: EmptyRequestState
}

interface Tracking_RestoreGoogleUserTracking extends Action<TrackingOperationType> {
    type: TrackingOperationType.RESTORE_GOOGLE_USER_TRACKING
    restoreGoogleUserTracking: RequestState<CoreGoogleUserTrackingConflict[]>
}

interface Tracking_MapGoogleUserTracking extends Action<TrackingOperationType> {
    type: TrackingOperationType.MAP_GOOGLE_USER_TRACKING
    mapGoogleUserTracking: RequestState<CoreGoogleUserTrackingConflict[]>
}

interface Tracking_UpgradeGoogleUserTracking extends Action<TrackingOperationType> {
    type: TrackingOperationType.UPGRADE_GOOGLE_USER_TRACKING
    upgradeGoogleUserTracking: RequestState<CoreGoogleUserTrackingConflict[]>
}

interface Tracking_UpdateEntraIdUserTracking extends Action<TrackingOperationType> {
    type: TrackingOperationType.UPDATE_ENTRAID_USER_TRACKING
    updateEntraIdUserTracking: RequestState<CoreEntraIdUserTrackingConflict[]>
}

interface Tracking_InactivateEntraIdUserTracking extends Action<TrackingOperationType> {
    type: TrackingOperationType.INACTIVATE_ENTRAID_USER_TRACKING
    inactivateEntraIdUserTracking: EmptyRequestState
}

interface Tracking_RestoreEntraIdUserTracking extends Action<TrackingOperationType> {
    type: TrackingOperationType.RESTORE_ENTRAID_USER_TRACKING
    restoreEntraIdUserTracking: RequestState<CoreEntraIdUserTrackingConflict[]>
}

interface Tracking_MapEntraIdUserTracking extends Action<TrackingOperationType> {
    type: TrackingOperationType.MAP_ENTRAID_USER_TRACKING
    mapEntraIdUserTracking: RequestState<CoreEntraIdUserTrackingConflict[]>
}

interface Tracking_InactivateAsmUserTracking extends Action<TrackingOperationType> {
    type: TrackingOperationType.INACTIVATE_ASM_USER_TRACKING
    inactivateAsmUserTracking: EmptyRequestState
}

interface Tracking_RestoreAsmUserTracking extends Action<TrackingOperationType> {
    type: TrackingOperationType.RESTORE_ASM_USER_TRACKING
    restoreAsmUserTracking: RequestState<CoreAsmUserTrackingConflict[]>
}

interface Tracking_MapAsmUserTracking extends Action<TrackingOperationType> {
    type: TrackingOperationType.MAP_ASM_USER_TRACKING
    mapAsmUserTracking: RequestState<CoreAsmUserTrackingConflict[]>
}

type TrackingOperation = Tracking_UpdateGoogleUserTracking
    | Tracking_InactivateGoogleUserTracking
    | Tracking_RestoreGoogleUserTracking
    | Tracking_MapGoogleUserTracking
    | Tracking_UpgradeGoogleUserTracking
    | Tracking_UpdateEntraIdUserTracking
    | Tracking_InactivateEntraIdUserTracking
    | Tracking_RestoreEntraIdUserTracking
    | Tracking_MapEntraIdUserTracking
    | Tracking_InactivateAsmUserTracking
    | Tracking_RestoreAsmUserTracking
    | Tracking_MapAsmUserTracking

export const UpdateGoogleUserTracking = (update: CoreGoogleUserTrackingUpdate, trackingId: string, dryRun: boolean) => (dispatch: Dispatch<Tracking_UpdateGoogleUserTracking>) => {
    dispatch({
        type: TrackingOperationType.UPDATE_GOOGLE_USER_TRACKING,
        updateGoogleUserTracking: { code: StatusCode.PENDING },
    });

    const url = `/api/core/trackingoperation/googleuser/${trackingId}/update?dryrun=${dryRun}`
    InstanceClient.post<CoreGoogleUserTrackingConflict[], CoreGoogleUserTrackingUpdate>(url, update)
        .then((res) => dispatch({
            type: TrackingOperationType.UPDATE_GOOGLE_USER_TRACKING,
            updateGoogleUserTracking: { code: StatusCode.COMPLETE, data: res.data }
        }))
        .catch((error) => dispatch({
            type: TrackingOperationType.UPDATE_GOOGLE_USER_TRACKING,
            updateGoogleUserTracking: { code: StatusCode.ERROR, error },
        }));
}

export const ClearUpdateGoogleUserTracking = () => (dispatch: Dispatch<Tracking_UpdateGoogleUserTracking>) => {
    dispatch({
        type: TrackingOperationType.UPDATE_GOOGLE_USER_TRACKING,
        updateGoogleUserTracking: { code: StatusCode.NONE },
    });
}

export const InactivateGoogleUserTracking = (trackingId: string) => (dispatch: Dispatch<Tracking_InactivateGoogleUserTracking>) => {
    dispatch({
        type: TrackingOperationType.INACTIVATE_GOOGLE_USER_TRACKING,
        inactivateGoogleUserTracking: { code: StatusCode.PENDING },
    });

    const url = `/api/core/trackingoperation/googleuser/${trackingId}/inactivate`;
    InstanceClient.post<any, any>(url)
        .then((_) => dispatch({
            type: TrackingOperationType.INACTIVATE_GOOGLE_USER_TRACKING,
            inactivateGoogleUserTracking: { code: StatusCode.COMPLETE }
        }))
        .catch((error) => dispatch({
            type: TrackingOperationType.INACTIVATE_GOOGLE_USER_TRACKING,
            inactivateGoogleUserTracking: { code: StatusCode.ERROR, error },
        }));
}

export const RestoreGoogleUserTracking = (trackingId: string, dryRun: boolean) => (dispatch: Dispatch<Tracking_RestoreGoogleUserTracking>) => {
    dispatch({
        type: TrackingOperationType.RESTORE_GOOGLE_USER_TRACKING,
        restoreGoogleUserTracking: { code: StatusCode.PENDING },
    });

    const url = `/api/core/trackingoperation/googleuser/${trackingId}/restore?dryrun=${dryRun}`;
    InstanceClient.post<CoreGoogleUserTrackingConflict[], undefined>(url)
        .then((res) => dispatch({
            type: TrackingOperationType.RESTORE_GOOGLE_USER_TRACKING,
            restoreGoogleUserTracking: { code: StatusCode.COMPLETE, data: res.data }
        }))
        .catch((error) => dispatch({
            type: TrackingOperationType.RESTORE_GOOGLE_USER_TRACKING,
            restoreGoogleUserTracking: { code: StatusCode.ERROR, error },
        }));
}

export const ClearRestoreGoogleUserTracking = () => (dispatch: Dispatch<Tracking_RestoreGoogleUserTracking>) => {
    dispatch({
        type: TrackingOperationType.RESTORE_GOOGLE_USER_TRACKING,
        restoreGoogleUserTracking: { code: StatusCode.NONE },
    });
}

export const MapGoogleUserTracking = (update: CoreGoogleUserTrackingMapping, trackingId: string, dryRun: boolean) => (dispatch: Dispatch<Tracking_MapGoogleUserTracking>) => {
    dispatch({
        type: TrackingOperationType.MAP_GOOGLE_USER_TRACKING,
        mapGoogleUserTracking: { code: StatusCode.PENDING },
    });

    const url = `/api/core/trackingoperation/googleuser/${trackingId}/map?dryrun=${dryRun}`
    InstanceClient.post<CoreGoogleUserTrackingConflict[], CoreGoogleUserTrackingMapping>(url, update)
        .then((res) => dispatch({
            type: TrackingOperationType.MAP_GOOGLE_USER_TRACKING,
            mapGoogleUserTracking: { code: StatusCode.COMPLETE, data: res.data }
        }))
        .catch((error) => dispatch({
            type: TrackingOperationType.MAP_GOOGLE_USER_TRACKING,
            mapGoogleUserTracking: { code: StatusCode.ERROR, error },
        }));
}

export const ClearMapGoogleUserTracking = () => (dispatch: Dispatch<Tracking_MapGoogleUserTracking>) => {
    dispatch({
        type: TrackingOperationType.MAP_GOOGLE_USER_TRACKING,
        mapGoogleUserTracking: { code: StatusCode.NONE },
    });
}

export const UpgradeGoogleUserTracking = (trackingId: string, selectedModule: string, dryRun: boolean) => (dispatch: Dispatch<Tracking_UpgradeGoogleUserTracking>) => {
    dispatch({
        type: TrackingOperationType.UPGRADE_GOOGLE_USER_TRACKING,
        upgradeGoogleUserTracking: { code: StatusCode.PENDING },
    });

    const url = `/api/core/trackingoperation/googleuser/${trackingId}/upgrade?selectedModule=${selectedModule}&dryrun=${dryRun}`
    InstanceClient.post<CoreGoogleUserTrackingConflict[], CoreGoogleUserTrackingMapping>(url)
        .then((res) => dispatch({
            type: TrackingOperationType.UPGRADE_GOOGLE_USER_TRACKING,
            upgradeGoogleUserTracking: { code: StatusCode.COMPLETE, data: res.data }
        }))
        .catch((error) => dispatch({
            type: TrackingOperationType.UPGRADE_GOOGLE_USER_TRACKING,
            upgradeGoogleUserTracking: { code: StatusCode.ERROR, error },
        }));
}

export const ClearUpgradeGoogleUserTracking = () => (dispatch: Dispatch<Tracking_UpgradeGoogleUserTracking>) => {
    dispatch({
        type: TrackingOperationType.UPGRADE_GOOGLE_USER_TRACKING,
        upgradeGoogleUserTracking: { code: StatusCode.NONE },
    });
}

export const UpdateEntraIdUserTracking = (update: CoreEntraIdUserTrackingUpdate, trackingId: string, dryRun: boolean) => (dispatch: Dispatch<Tracking_UpdateEntraIdUserTracking>) => {
    dispatch({
        type: TrackingOperationType.UPDATE_ENTRAID_USER_TRACKING,
        updateEntraIdUserTracking: { code: StatusCode.PENDING },
    });

    const url = `/api/core/trackingoperation/entraiduser/${trackingId}/update?dryrun=${dryRun}`
    InstanceClient.post<CoreEntraIdUserTrackingConflict[], CoreEntraIdUserTrackingUpdate>(url, update)
        .then((res) => dispatch({
            type: TrackingOperationType.UPDATE_ENTRAID_USER_TRACKING,
            updateEntraIdUserTracking: { code: StatusCode.COMPLETE, data: res.data }
        }))
        .catch((error) => dispatch({
            type: TrackingOperationType.UPDATE_ENTRAID_USER_TRACKING,
            updateEntraIdUserTracking: { code: StatusCode.ERROR, error },
        }));
}

export const ClearUpdateEntraIdUserTracking = () => (dispatch: Dispatch<Tracking_UpdateEntraIdUserTracking>) => {
    dispatch({
        type: TrackingOperationType.UPDATE_ENTRAID_USER_TRACKING,
        updateEntraIdUserTracking: { code: StatusCode.NONE },
    });
}

export const InactivateEntraIdUserTracking = (trackingId: string) => (dispatch: Dispatch<Tracking_InactivateEntraIdUserTracking>) => {
    dispatch({
        type: TrackingOperationType.INACTIVATE_ENTRAID_USER_TRACKING,
        inactivateEntraIdUserTracking: { code: StatusCode.PENDING },
    });

    const url = `/api/core/trackingoperation/entraiduser/${trackingId}/inactivate`;
    InstanceClient.post<any, any>(url)
        .then((_) => dispatch({
            type: TrackingOperationType.INACTIVATE_ENTRAID_USER_TRACKING,
            inactivateEntraIdUserTracking: { code: StatusCode.COMPLETE }
        }))
        .catch((error) => dispatch({
            type: TrackingOperationType.INACTIVATE_ENTRAID_USER_TRACKING,
            inactivateEntraIdUserTracking: { code: StatusCode.ERROR, error },
        }));
}

export const RestoreEntraIdUserTracking = (trackingId: string, dryRun: boolean) => (dispatch: Dispatch<Tracking_RestoreEntraIdUserTracking>) => {
    dispatch({
        type: TrackingOperationType.RESTORE_ENTRAID_USER_TRACKING,
        restoreEntraIdUserTracking: { code: StatusCode.PENDING },
    });

    const url = `/api/core/trackingoperation/entraiduser/${trackingId}/restore?dryrun=${dryRun}`;
    InstanceClient.post<CoreEntraIdUserTrackingConflict[], undefined>(url)
        .then((res) => dispatch({
            type: TrackingOperationType.RESTORE_ENTRAID_USER_TRACKING,
            restoreEntraIdUserTracking: { code: StatusCode.COMPLETE, data: res.data }
        }))
        .catch((error) => dispatch({
            type: TrackingOperationType.RESTORE_ENTRAID_USER_TRACKING,
            restoreEntraIdUserTracking: { code: StatusCode.ERROR, error },
        }));
}

export const ClearRestoreEntraIdUserTracking = () => (dispatch: Dispatch<Tracking_RestoreEntraIdUserTracking>) => {
    dispatch({
        type: TrackingOperationType.RESTORE_ENTRAID_USER_TRACKING,
        restoreEntraIdUserTracking: { code: StatusCode.NONE },
    });
}

export const MapEntraIdUserTracking = (update: CoreEntraIdUserTrackingMapping, trackingId: string, dryRun: boolean) => (dispatch: Dispatch<Tracking_MapEntraIdUserTracking>) => {
    dispatch({
        type: TrackingOperationType.MAP_ENTRAID_USER_TRACKING,
        mapEntraIdUserTracking: { code: StatusCode.PENDING },
    });

    const url = `/api/core/trackingoperation/entraiduser/${trackingId}/map?dryrun=${dryRun}`
    InstanceClient.post<CoreEntraIdUserTrackingConflict[], CoreEntraIdUserTrackingMapping>(url, update)
        .then((res) => dispatch({
            type: TrackingOperationType.MAP_ENTRAID_USER_TRACKING,
            mapEntraIdUserTracking: { code: StatusCode.COMPLETE, data: res.data }
        }))
        .catch((error) => dispatch({
            type: TrackingOperationType.MAP_ENTRAID_USER_TRACKING,
            mapEntraIdUserTracking: { code: StatusCode.ERROR, error },
        }));
}

export const ClearMapEntraIdUserTracking = () => (dispatch: Dispatch<Tracking_MapEntraIdUserTracking>) => {
    dispatch({
        type: TrackingOperationType.MAP_ENTRAID_USER_TRACKING,
        mapEntraIdUserTracking: { code: StatusCode.NONE },
    });
}

export const InactivateAsmUserTracking = (trackingId: string) => (dispatch: Dispatch<Tracking_InactivateAsmUserTracking>) => {
    dispatch({
        type: TrackingOperationType.INACTIVATE_ASM_USER_TRACKING,
        inactivateAsmUserTracking: { code: StatusCode.PENDING },
    });

    const url = `/api/core/trackingoperation/asmuser/${trackingId}/inactivate`;
    InstanceClient.post<any, any>(url)
        .then((_) => dispatch({
            type: TrackingOperationType.INACTIVATE_ASM_USER_TRACKING,
            inactivateAsmUserTracking: { code: StatusCode.COMPLETE }
        }))
        .catch((error) => dispatch({
            type: TrackingOperationType.INACTIVATE_ASM_USER_TRACKING,
            inactivateAsmUserTracking: { code: StatusCode.ERROR, error },
        }));
}

export const RestoreAsmUserTracking = (trackingId: string, dryRun: boolean) => (dispatch: Dispatch<Tracking_RestoreAsmUserTracking>) => {
    dispatch({
        type: TrackingOperationType.RESTORE_ASM_USER_TRACKING,
        restoreAsmUserTracking: { code: StatusCode.PENDING },
    });

    const url = `/api/core/trackingoperation/asmuser/${trackingId}/restore?dryrun=${dryRun}`;
    InstanceClient.post<CoreAsmUserTrackingConflict[], undefined>(url)
        .then((res) => dispatch({
            type: TrackingOperationType.RESTORE_ASM_USER_TRACKING,
            restoreAsmUserTracking: { code: StatusCode.COMPLETE, data: res.data }
        }))
        .catch((error) => dispatch({
            type: TrackingOperationType.RESTORE_ASM_USER_TRACKING,
            restoreAsmUserTracking: { code: StatusCode.ERROR, error },
        }));
}

export const ClearRestoreAsmUserTracking = () => (dispatch: Dispatch<Tracking_RestoreAsmUserTracking>) => {
    dispatch({
        type: TrackingOperationType.RESTORE_ASM_USER_TRACKING,
        restoreAsmUserTracking: { code: StatusCode.NONE },
    });
}

export const MapAsmUserTracking = (update: CoreAsmUserTrackingMapping, trackingId: string, dryRun: boolean) => (dispatch: Dispatch<Tracking_MapAsmUserTracking>) => {
    dispatch({
        type: TrackingOperationType.MAP_ASM_USER_TRACKING,
        mapAsmUserTracking: { code: StatusCode.PENDING },
    });

    const url = `/api/core/trackingoperation/asmuser/${trackingId}/map?dryrun=${dryRun}`
    InstanceClient.post<CoreAsmUserTrackingConflict[], CoreAsmUserTrackingMapping>(url, update)
        .then((res) => dispatch({
            type: TrackingOperationType.MAP_ASM_USER_TRACKING,
            mapAsmUserTracking: { code: StatusCode.COMPLETE, data: res.data }
        }))
        .catch((error) => dispatch({
            type: TrackingOperationType.MAP_ASM_USER_TRACKING,
            mapAsmUserTracking: { code: StatusCode.ERROR, error },
        }));
}

export const ClearMapAsmUserTracking = () => (dispatch: Dispatch<Tracking_MapAsmUserTracking>) => {
    dispatch({
        type: TrackingOperationType.MAP_ASM_USER_TRACKING,
        mapAsmUserTracking: { code: StatusCode.NONE },
    });
}

const initialState: TrackingOperationState = {
    updateGoogleUserTracking: { code: StatusCode.NONE },
    inactivateGoogleUserTracking: { code: StatusCode.NONE },
    restoreGoogleUserTracking: { code: StatusCode.NONE },
    mapGoogleUserTracking: { code: StatusCode.NONE },
    upgradeGoogleUserTracking: { code: StatusCode.NONE },
    updateEntraIdUserTracking: { code: StatusCode.NONE },
    inactivateEntraIdUserTracking: { code: StatusCode.NONE },
    restoreEntraIdUserTracking: { code: StatusCode.NONE },
    mapEntraIdUserTracking: { code: StatusCode.NONE },
    inactivateAsmUserTracking: { code: StatusCode.NONE },
    restoreAsmUserTracking: { code: StatusCode.NONE },
    mapAsmUserTracking: { code: StatusCode.NONE },
}

export const reducer: Reducer<TrackingOperationState, TrackingOperation> = (state, action) => {
    state = state || initialState;

    switch (action.type) {
        case TrackingOperationType.UPDATE_GOOGLE_USER_TRACKING:
            return {
                ...state,
                updateGoogleUserTracking: action.updateGoogleUserTracking,
            };
        case TrackingOperationType.INACTIVATE_GOOGLE_USER_TRACKING:
            return {
                ...state,
                inactivateGoogleUserTracking: action.inactivateGoogleUserTracking,
            };
        case TrackingOperationType.RESTORE_GOOGLE_USER_TRACKING:
            return {
                ...state,
                restoreGoogleUserTracking: action.restoreGoogleUserTracking,
            };
        case TrackingOperationType.MAP_GOOGLE_USER_TRACKING:
            return {
                ...state,
                mapGoogleUserTracking: action.mapGoogleUserTracking,
            };
        case TrackingOperationType.UPGRADE_GOOGLE_USER_TRACKING:
            return {
                ...state,
                upgradeGoogleUserTracking: action.upgradeGoogleUserTracking,
            };
        case TrackingOperationType.UPDATE_ENTRAID_USER_TRACKING:
            return {
                ...state,
                updateEntraIdUserTracking: action.updateEntraIdUserTracking,
            }
        case TrackingOperationType.INACTIVATE_ENTRAID_USER_TRACKING:
            return {
                ...state,
                inactivateEntraIdUserTracking: action.inactivateEntraIdUserTracking,
            }
        case TrackingOperationType.RESTORE_ENTRAID_USER_TRACKING:
            return {
                ...state,
                restoreEntraIdUserTracking: action.restoreEntraIdUserTracking,
            }
        case TrackingOperationType.MAP_ENTRAID_USER_TRACKING:
            return {
                ...state,
                mapEntraIdUserTracking: action.mapEntraIdUserTracking,
            }
        case TrackingOperationType.INACTIVATE_ASM_USER_TRACKING:
            return {
                ...state,
                inactivateAsmUserTracking: action.inactivateAsmUserTracking,
            };
        case TrackingOperationType.RESTORE_ASM_USER_TRACKING:
            return {
                ...state,
                restoreAsmUserTracking: action.restoreAsmUserTracking,
            };
        case TrackingOperationType.MAP_ASM_USER_TRACKING:
            return {
                ...state,
                mapAsmUserTracking: action.mapAsmUserTracking,
            };
        default:
            return state;
    }
}